import { usecount } from '@/store/config';
import { useAuthService } from '@/utils/useAuthService';
import * as icons from '@ant-design/icons';
import NiceModal from '@ebay/nice-modal-react';

import { history, Link, matchRoutes, Navigate } from '@umijs/max';
import { Badge, ConfigProvider, message } from 'antd';
import { get } from 'lodash-es';
import React, { ReactNode, useEffect, useMemo, useReducer } from 'react';
import NoAccess from './pages/403';
import NoFind from './pages/404';

// @ts-ignore
import { MenuDataItem } from '@ant-design/pro-components';
import { User } from 'oidc-client-ts';
import { AbbTextSvg } from './assets/icon';
import { HeaderTitleRender } from './layouts/headerTitleRender';
import { RightContentRender } from './layouts/rightContentRender';
import { MenuItem } from './services/obas-api/menu';
import { getCurrentUserInfo } from './services/obas-api/user';
import { GlobalStyles } from './styles';
import { theme } from './styles/theme';
import {
  applyApiMenuToLayoutMenu,
  getAsyncMenuData,
  loadAppMenuOnce,
  subscribeMenuRevision,
} from './utils/appMenuLoader';
import {
  APP_TITLE,
  getPageTitleForPathname,
  updateDocumentTitle,
} from './utils/pageTitle';
import { breadcrumbItemRender } from './utils/breadcrumbItemRender';
import requestConfig from './utils/requestConfig';
import { startExportTaskPolling } from './utils/exportTaskPoller';

// 全局提示默认停留 5 秒
message.config({ duration: 5 });

// 记录用户信息
let userInfo: any = { permissions: [] };

export const request = requestConfig;

export async function getInitialState(): Promise<any> {
  localStorage.setItem(
    'obas-userAuthorization',
    JSON.stringify(userInfo.permissions),
  );

  // 与 render() 内 loadAppMenuOnce 合并为单次请求（并发时也只发一次 HTTP）
  let menu: MenuItem[] = [];
  try {
    menu = await loadAppMenuOnce();
  } catch (e) {}

  return {
    ...(userInfo || {}),
    // 是否ABB用户标识
    isABB: userInfo?.userType === 'ABB',
    // 统一暴露给前端使用的展示名称
    nameCn: userInfo?.name,
    // 对应SAP用户名（与系统管理-用户管理中的sapShortName字段对应）
    sapShortName: userInfo?.sapShortName,
    // 兼容字段：部分页面可能直接使用userName
    userName: userInfo?.userName || userInfo?.name,
    menu,
  };
}

// @ts-ignore
export const layout = ({ initialState, ...props }) => {
  const { count, getcount } = usecount();
  const [menuRenderKey, bumpMenuRender] = useReducer((n: number) => n + 1, 0);

  useEffect(() => {
    getcount();
  }, []);

  useEffect(() => subscribeMenuRevision(bumpMenuRender), []);

  const menuDataRender = useMemo(
    () => (menuData: MenuDataItem[]) => applyApiMenuToLayoutMenu(menuData),
    [initialState?.menu, menuRenderKey],
  );

  return {
    logo: AbbTextSvg,
    style: {
      height: '100vh',
    },
    pageTitleRender: () =>
      getPageTitleForPathname(history.location.pathname),

    menu: {
      locale: false,
      collapsedShowGroupTitle: true,
      autoClose: false,
    },
    menuDataRender,
    subMenuItemRender: (item: MenuDataItem, dom: ReactNode) => {
      if (item.name === '流程审批管理' && count) {
        return (
          <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            {dom}
            <Badge 
              count={count} 
              style={{ 
                backgroundColor: '#FF6B6B',
                borderRadius: '15px',
                fontSize: '8px',
                minWidth: '16px',
                height: '16px',
                lineHeight: '16px',
              }} 
            />
          </div>
        );
      }
      return dom;
    },
    menuItemRender: (item: MenuDataItem, dom: ReactNode) => {
      if (item.name === '待审批' && count) {
        return (
          <Link to={item.path as string}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
              {dom}
              <Badge 
                count={count} 
                style={{ 
                 backgroundColor: '#FF6B6B',
                borderRadius: '15px',
                fontSize: '8px',
                minWidth: '16px',
                height: '16px',
                lineHeight: '16px',
                }} 
              />
            </div>
          </Link>
        );
      }

      return <Link to={item.path as string}>{dom}</Link>;
    },
    itemRender: breadcrumbItemRender,

    collapsedButtonRender: false,
    token: {
      header: {
        // heightLayoutHeader: 48,
      },
      sider: {
        colorMenuBackground: 'white',
        colorTextMenuSelected: '#3366FF',
        colorBgMenuItemSelected: 'rgba(51, 102, 255, 0.08)',
        colorTextMenu: 'rgba(31, 31, 31, 1)',
      },
    },
    breakpoint: true,
    // 默认收齐彩蛋选择框
    // defaultCollapsed: true,
    fixedHeader: false,
    // fixSiderbar: true,
    // splitMenus: true,
    layout: 'top',
    // siderWidth: 208,
    // 自定义 403 页面
    unAccessible: <NoAccess />,
    // // 自定义 404 页面
    noFound: <NoFind />,
    headerTitleRender: (logo: any) => {
      return <HeaderTitleRender logo={logo} title={APP_TITLE} />;
    },
    rightContentRender: (props: any) => {
      return (
        <RightContentRender
          {...props}
          count={count}
          initialState={initialState}
        />
      );
    },
  };
};

// @ts-ignore
export function onRouteChange({ clientRoutes, location }) {
  const matches = matchRoutes(clientRoutes, location.pathname);
  if (!matches?.length) {
    history.push('/404');
    return;
  }
  updateDocumentTitle(location.pathname, matches);
  document.getElementById('scrollId')?.scrollTo(0, 0);
}

// 处理一下 登陆校验 和  获取用户信息
export const render = async (oldRender: () => void) => {
  const { getUser, login, signinCallback, startTokenAutoRefresh } =
    useAuthService();
  if (window.location.pathname === '/login') {
    await login();
    oldRender();
    return true;
  }

  const userInfoData = await getUser();
  // 处理账号禁用 接口无权限一直loading问题
  if (window.location.pathname === '/403' && !userInfoData?.profile?.name) {
    // oldRender();
    message.error('无权限访问');
    return true;
  } else if (window.location.pathname === '/403') {
    //处理接口403 把用户信息清空问题
    // const res: any = await getCurrentUserInfo();
    // if (res?.isSuccess) {
    //   userInfo = res?.data || {};
    //   oldRender();
    // }
    return true;
  }

  let auth: User | null = null;
  try {
    auth = await signinCallback();
  } catch (error) {
    auth = await getUser();

    if (!auth) {
      // 存储从邮件过来的链接地址
      localStorage.setItem(
        'obas-emailLink',
        JSON.stringify(window.location.href),
      );
      await login();
    }
  }

  if (auth) {
    try {
      // 获取用户信息
      const res: any = await getCurrentUserInfo();
      await loadAppMenuOnce();
      if (res?.isSuccess) {
        userInfo = res?.data || {};
        // if (window.location.pathname === '/login' || !res?.data.isEnabled) {
        //   history.push('/');
        // }
      }
    } catch (e) {}
    // 从邮件过来的链接地址
    const url = localStorage.getItem('obas-emailLink')!;
    if (url) {
      history.push(JSON.parse(url));
      localStorage.removeItem('obas-emailLink');
    }
  }

  // 启动token自动刷新（仅在成功认证后）
  if (auth) {
    startTokenAutoRefresh();
    // 登录后查询导出任务：Status=2 自动下载，存在排队/生成中则轮询
    startExportTaskPolling();
  }

  oldRender();
};

const setReplace = (node: any) => {
  const firstChild = get(node.children, '[0]');

  let path = firstChild?.path;

  if (firstChild?.children && firstChild?.children.length > 0) {
    path = get(firstChild.children, '[0].name')
      ? get(firstChild.children, '[0].path')
      : get(firstChild.children, '[1].path');
  }

  if (path) {
    node.children.unshift({
      path: node.path,
      element: <Navigate to={path} />,
    });
  }
};

// 这里处理下 菜单权限 控制菜单的显示和消失
// @ts-ignore
export function patchClientRoutes({ routes }) {
  function normalizePath(p: string | undefined) {
    if (!p) return '';
    // ensure leading '/'
    let s = p.startsWith('/') ? p : `/${p}`;
    // remove querystring/hash
    s = s.split(/[?#]/)[0];
    // remove trailing slash
    s = s.replace(/\/$/, '');
    // normalize index paths
    s = s.replace(/\/index$/, '');
    // keep path params like '/:id' or '/:itemId' so parameterized routes match strictly
    // collapse multiple slashes
    s = s.replace(/\/+/g, '/');
    return s;
  }

  // (isPathMatch removed — we use strict mapping via route/path keys)

  function findMenuItemByPath(
    items: MenuItem[] | undefined,
    path: string,
  ): MenuItem | undefined {
    // fallback to recursive search in case flattening isn't provided
    if (!items || !path) return undefined;
    const norm = normalizePath(path);
    for (const it of items) {
      const itPath = normalizePath(it.path);
      if (itPath === norm) return it;
      const found = findMenuItemByPath(it.children, path);
      if (found) return found;
    }
    return undefined;
  }

  function flattenMenuItems(items: MenuItem[] | undefined) {
    const byRoute = new Map<string, MenuItem>();
    const byPath = new Map<string, MenuItem>();
    if (!items || items.length === 0)
      return { byRoute, byPath, roots: items || [] };
    const stack = [...items];
    while (stack.length) {
      const it = stack.pop()!;
      const menuRoute = (it as any).route || it.path;
      const r = normalizePath(menuRoute);
      const p = normalizePath(it.path);
      if (r && !byRoute.has(r)) byRoute.set(r, it);
      if (p && !byPath.has(p)) byPath.set(p, it);
      if (it.children && it.children.length) stack.push(...it.children);
    }
    return { byRoute, byPath, roots: items };
  }

  function updateRouteNamesFromMenu(
    routesList: any[],
    flat: {
      byRoute: Map<string, MenuItem>;
      byPath: Map<string, MenuItem>;
      roots: MenuItem[];
    },
  ) {
    if (!routesList || !flat) return;
    for (const r of routesList) {
      if (!r) continue;
      // First try to match by route mapping if available
      const routeKey = normalizePath((r as any).route || r.path);
      let menuMatch = flat.byRoute.get(routeKey) || flat.byPath.get(routeKey);
      if (!menuMatch) {
        // fall back to recursive search if not found in flat maps
        menuMatch = findMenuItemByPath(flat.roots, r.path);
      }

      if (menuMatch && menuMatch.name) {
        r.name = menuMatch.name;
        // menuMatch.icon 可能是：
        // - Ant Design 图标的名称字符串（如 'UserOutlined'），需要把它映射为 ReactNode
        // - 已经是 ReactNode（例如自定义的 <MyIcon/>），直接使用
        // - 或者是其它形式（字符串 URL、SVG 等），直接按需处理
        const iconFromMenu = (menuMatch as any).icon;
        if (iconFromMenu) {
          // 如果是字符串，尝试从 @ant-design/icons 中取对应组件
          if (typeof iconFromMenu === 'string') {
            const IconComp = (icons as any)[iconFromMenu];
            if (IconComp) {
              r.icon = React.createElement(IconComp);
            } else {
              // 不是内置 ant icon 名称，直接把字符串放到 icon（可能是 url 或自定义标识）
              r.icon = iconFromMenu;
            }
          } else {
            // 已经是 ReactNode 或组件，直接使用
            r.icon = iconFromMenu as any;
          }
        }
      }
      if (r.routes && r.routes.length) {
        // always search globally using the flattened maps to avoid altering hierarchy
        updateRouteNamesFromMenu(r.routes, flat);
      }
    }
  }

  function filterTreeByKeys(tree: any[], keyArray: any) {
    // return tree;
    return tree.filter((node) => {
      if (node.path === '/' && node.routes && node.routes.length) {
        // 对菜单做处理：先扁平化菜单为 map，提高匹配精确度
        const flat = flattenMenuItems(getAsyncMenuData());
        console.log(tree, 'tree', flat);

        updateRouteNamesFromMenu(node.routes || [], flat);
      }
      return true;
    });
  }

  try {
    filterTreeByKeys(routes, userInfo?.permissions);
  } catch (error) {}
}
// @ts-ignore
const WrapProvider = ({ children }) => (
  <NiceModal.Provider>
    <ConfigProvider button={{ autoInsertSpace: false }} theme={theme}>
      <GlobalStyles />
      {children}
    </ConfigProvider>
  </NiceModal.Provider>
);

export function rootContainer(
  container:
    | string
    | number
    | boolean
    | React.ReactElement<any, string | React.JSXElementConstructor<any>>
    | Iterable<React.ReactNode>
    | React.ReactPortal
    | null
    | undefined,
) {
  return React.createElement(WrapProvider, null, container);
}
